import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Box } from '@mui/material'
import { ShieldExclamationIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline'

import Button from '../interaxDesignSystemComponents/Button'
import { INTERAX_COLORS } from '../constants/colors'
import { useTranslation } from '../shared/hooks/useIntractionTranslate'
import useAppState from '../hooks/useAppState'
import { useLogout } from '../hooks/useLogout'
import { getActiveOrgRole } from '../features/organization/orgStateHelpers'
import { getDefaultRouteForRole } from '../utils/routeProtection'

/**
 * Rendered by ProtectedRoute when a signed-in user lacks the required role,
 * or has no active organization role at all. Provides a role-aware "home"
 * target and a logout escape hatch so a wedged user isn't stuck on this page.
 */
export const NotAuthorisedPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { state } = useAppState()
  const logout = useLogout()

  const activeOrgRole = state.user ? getActiveOrgRole(state.user) : undefined
  // When the user has no active role, "home" would loop back through SignIn
  // into the same blocked route. /login is the only useful destination.
  const homePath = activeOrgRole ? getDefaultRouteForRole(activeOrgRole) : '/login'

  const handleGoBack = () => navigate(-1)
  const handleGoHome = () => navigate(homePath, { replace: true })
  const handleLogout = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: INTERAX_COLORS.offBlack,
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 4, sm: 6, md: 8 }
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 520,
          backgroundColor: INTERAX_COLORS.heritageBlue,
          border: `1px solid ${INTERAX_COLORS.white10}`,
          borderRadius: 3,
          px: { xs: 3, sm: 4, md: 5 },
          py: { xs: 4, sm: 5, md: 6 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center'
        }}
      >
        <Box
          sx={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            backgroundColor: 'rgba(248, 113, 113, 0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3
          }}
        >
          <ShieldExclamationIcon style={{ width: 56, height: 56, color: INTERAX_COLORS.redError }} />
        </Box>

        <Box
          component="h1"
          sx={{
            fontSize: { xs: '24px', sm: '28px', md: '32px' },
            fontWeight: 700,
            color: INTERAX_COLORS.white,
            m: 0,
            mb: 1.5
          }}
        >
          {t('accessDenied.title')}
        </Box>

        <Box
          component="p"
          sx={{
            fontSize: { xs: '14px', sm: '15px', md: '16px' },
            color: INTERAX_COLORS.white70,
            lineHeight: 1.5,
            m: 0,
            mb: 4,
            maxWidth: 420
          }}
        >
          {t('accessDenied.description')}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 2 },
            width: '100%',
            justifyContent: 'center'
          }}
        >
          <Button variant="secondary" onClick={handleGoBack} data-testid="access-denied-go-back">
            {t('accessDenied.goBack')}
          </Button>
          <Button variant="primary" onClick={handleGoHome} data-testid="access-denied-go-home">
            {t('accessDenied.goHome')}
          </Button>
        </Box>

        <Box
          sx={{
            mt: 3,
            pt: 3,
            width: '100%',
            borderTop: `1px solid ${INTERAX_COLORS.white10}`,
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Button
            variant="link"
            onClick={handleLogout}
            icon={<ArrowRightOnRectangleIcon style={{ width: 16, height: 16 }} />}
            iconPosition="left"
          >
            {t('accessDenied.logout')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
